import { FunctionComponent, useEffect, useState } from 'react'
import styles from './rpeComplementaryInformations.module.scss'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptions } from '@/types/Select'
import { UPDATE_CHILD_ASSMAT_PRIORITY_GROUP, UPDATE_CHILD_ASSMAT_SECTOR } from '@/modules/API/parent/assmatHandler'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Child, ChildDaycare, ChildSection } from '@/types/Child'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { ParentDaycare } from '@/types/Parent'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { OccasionalSlot } from '@/types/Slots.ts'
import { GET_LAST_OCCASIONAL_SLOT } from '@/modules/API/slot/occasionalSlotHandler.ts'

interface RpeComplementaryInfosProps {
    sections: SelectOptions<number>
    groups: SelectOptions<number>
    daycare: ParentDaycare
    parentId: number
    childs: Array<Child>
    selectOptionsDaycare: SelectOptions<number>
    handleSetSelectedDaycare: (value: number) => void
    canUpdate: boolean
}

const RpeComplementaryInformations: FunctionComponent<RpeComplementaryInfosProps> = ({
    sections,
    groups,
    daycare,
    parentId,
    childs,
    selectOptionsDaycare,
    handleSetSelectedDaycare,
    canUpdate
}: RpeComplementaryInfosProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [sectionsError, setSectionsError] = useState<boolean>(false)
    const [groupsError, setGroupsError] = useState<boolean>(false)

    const [selectedSection, setSelectedSection] = useState<number[]>([])
    const [primarySection, setPrimarySection] = useState<number | undefined>()

    const [groupId, setGroupEdit] = useState<number | undefined>()

    const [selectedSectionBase, setSelectedSectionBase] = useState<number[]>([])
    const [primarySectionBase, setPrimarySectionBase] = useState<number | undefined>()

    const [lastAnimationSlots, setLastAnimationSlots] = useState<OccasionalSlot | undefined>(undefined)

    const [groupBase, setGroupBase] = useState<number | undefined>()

    useEffect(() => {
        const childsDaycare: (ChildDaycare | undefined)[] = childs.map((child: Child): ChildDaycare | undefined =>
            child.daycares.find((d: ChildDaycare): boolean => d.daycareId === daycare?.daycareId)
        )

        const childSections: ChildSection[] = childsDaycare
            .filter((child: ChildDaycare | undefined): boolean => child?.visible === true)
            .map((child: ChildDaycare | undefined): ChildSection[] => (child ? child?.sections : []))
            .flat()

        const uniqueSectionsIds: number[] = Array.from(
            new Set(childSections.map((section: ChildSection): number => section.sectionId))
        )
        const uniquePrimarySectionsId: number | undefined = childSections.find(
            (section: ChildSection): boolean => section.isPrimary
        )?.sectionId

        const groupsIds: (number | undefined)[] = childsDaycare
            .map((child: ChildDaycare | undefined): number | undefined => child?.group?.groupId)
            .filter((group: number | undefined): boolean => group !== null && group !== undefined)
        const uniqueGroupsIds: (number | null | undefined)[] = Array.from(new Set(groupsIds))

        setSelectedSection(uniqueSectionsIds || [])
        setSelectedSectionBase(uniqueSectionsIds || [])

        if (uniquePrimarySectionsId) {
            setSectionsError(false)
            setPrimarySection(uniquePrimarySectionsId)
            setPrimarySectionBase(uniquePrimarySectionsId)
        }

        if (uniqueGroupsIds.length === 1) {
            setGroupsError(false)
            setGroupEdit(uniqueGroupsIds[0] ?? undefined)
            setGroupBase(uniqueGroupsIds[0] ?? undefined)
        } else if (uniqueGroupsIds.length > 1) {
            setGroupsError(true)
            setGroupEdit(undefined)
            setGroupBase(undefined)
        }
    }, [childs])

    const selectOptionsSections: SelectOptions<number> = [
        {
            options: sections.flatMap((section) => section.options)
        }
    ]

    const selectOptionsGroups: SelectOptions<number | undefined> = [
        {
            options: [
                {
                    name: t('noPriorityGroup'),
                    value: undefined
                }
            ]
        },
        {
            options: groups.flatMap((group) => group.options)
        }
    ]

    const selectOptionsPrimarySection: SelectOptions<number | undefined> = [
        {
            options: sections.flatMap((section) => section.options)
        }
    ]

    const [getLastOccasionalSlot, { data: dataGetLastOccasionalSlot }] = useLazyQuery(GET_LAST_OCCASIONAL_SLOT)

    useEffect(() => {
        if (dataGetLastOccasionalSlot?.getLastOccasionalSlot) {
            setLastAnimationSlots(dataGetLastOccasionalSlot.getLastOccasionalSlot)
        } else {
            setLastAnimationSlots(undefined)
        }
    }, [dataGetLastOccasionalSlot])

    useEffect(() => {
        getLastOccasionalSlot({
            variables: {
                parentId: parentId,
                daycareId: daycare.daycareId
            }
        })
    }, [parentId, daycare.daycareId])

    const [updateAssMatSector] = useMutation(UPDATE_CHILD_ASSMAT_SECTOR, {
        variables: {
            daycareId: daycare.daycareId,
            parentId: parentId,
            sectionIds: selectedSection.map((sectionId: number) => sectionId),
            primarySectionId: selectedSection.length > 1 && primarySection ? primarySection : selectedSection[0]
        },
        onCompleted: () => {
            setPrimarySectionBase(selectedSection.length > 1 && primarySection ? primarySection : selectedSection[0])
            setSelectedSectionBase(selectedSection)

            setSectionsError(false)

            showNotificationSuccess(t('sectorUpdated'))
        },
        onError: () => {
            setSectionsError(true)

            showNotificationError(t('errorUpdatedSector'))
        }
    })

    async function handleSelectedSection(value: number[]): Promise<void> {
        setSelectedSection(value)
    }

    const [updateAssMatPriorityGroup] = useMutation(UPDATE_CHILD_ASSMAT_PRIORITY_GROUP, {
        variables: {
            daycareId: daycare?.daycareId,
            parentId: parentId,
            groupId: groupId
        },
        onCompleted: () => {
            setGroupBase(groupId)

            setGroupsError(false)
            showNotificationSuccess(t('groupUpdated'))
        },
        onError: () => {
            setGroupsError(true)
            showNotificationError(t('errorUpdatedGroup'))
        }
    })

    async function handleUpdate(): Promise<void> {
        await Promise.all([updateAssMatSector(), updateAssMatPriorityGroup()])
    }

    const disabled: boolean =
        !canUpdate ||
        (groupId === groupBase &&
            JSON.stringify(selectedSection) === JSON.stringify(selectedSectionBase) &&
            primarySection === primarySectionBase) ||
        (selectedSection.length > 1 && !primarySection)

    return (
        <div className={styles.Form}>
            <div className={styles.TitleAndButton}>
                <Title text={t('daycareInformation')} />
                {selectOptionsDaycare[0].options.length > 1 && (
                    <div className={styles.Input}>
                        <SelectUnique
                            selectOptions={selectOptionsDaycare}
                            selectValue={daycare.daycareId}
                            setSelectValue={handleSetSelectedDaycare}
                            label={t('structure')}
                            className={styles.Select}
                            formClassName={styles.UserInput}
                        />
                    </div>
                )}
            </div>
            <div className={styles.InputContainer}>
                <div className={styles.Input}>
                    <SelectUnique
                        selectOptions={selectOptionsGroups}
                        selectValue={groupId}
                        setSelectValue={setGroupEdit}
                        label={t('priorityGroup')}
                        formClassName={styles.UserInput}
                        readOnly={!canUpdate}
                    />
                    <SelectMultiple
                        selectOptions={selectOptionsSections}
                        selectValues={selectedSection}
                        setSelectValues={handleSelectedSection}
                        readOnly={!canUpdate}
                        label={daycare.isRpeOrLaep ? t('sector') : t('section')}
                        className={styles.UserInput}
                    />
                </div>
                {selectedSection.length > 1 && (
                    <div className={styles.Input}>
                        <SelectUnique
                            selectOptions={selectOptionsPrimarySection}
                            selectValue={primarySection}
                            required
                            setSelectValue={setPrimarySection}
                            label={daycare.isRpeOrLaep ? t('primarySector') : t('primarySection')}
                            formClassName={styles.UserInput}
                            readOnly={!canUpdate}
                        />
                    </div>
                )}
                {canUpdate && (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'update'}
                            className={styles.Button}
                            disabled={disabled}
                            onClick={handleUpdate}
                        />
                    </div>
                )}
                {sectionsError && <ErrorMessage error={t('pleaseSelectSector')} />}
                {groupsError && <ErrorMessage error={t('pleaseSelectGroupPriority')} />}
            </div>
            {lastAnimationSlots && (
                <div className={styles.TextContainer}>
                    {t('lastAnimationSlots')}
                    {[
                        format(parse(lastAnimationSlots.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                            locale: getLocalFromLanguage()
                        }),
                        lastAnimationSlots.childName,
                        lastAnimationSlots.placeName
                    ]
                        .filter(Boolean)
                        .join(' - ')}
                </div>
            )}
        </div>
    )
}

export default RpeComplementaryInformations
